import { Typography } from "@outschool/backpack";
import { ActivityListing_ListingFragment } from "@outschool/gql-frontend-generated";
import { useProductClicked } from "@outschool/ui-analytics";
import { TrackingProperties } from "@outschool/ui-legacy-component-library";
import { useLinkComponent } from "@outschool/ui-utils";
import { SystemStyleObject } from "@styled-system/css";
import React, { useMemo } from "react";
import { useLocation } from "react-router";

function useLinkPath<T>({ activity }: T & { activity: { path: string } }) {
  const { search } = useLocation();
  return useMemo(() => {
    const learnerPlanGoalUid = new URLSearchParams(search).get(
      "learnerPlanGoalUid",
    );
    if (learnerPlanGoalUid) {
      return activity.path + `?learnerPlanGoalUid=${learnerPlanGoalUid}`;
    }
    return activity.path;
  }, [activity.path, search]);
}

interface TitleProps {
  activity: ActivityListing_ListingFragment["activity"];
  insertionId?: ActivityListing_ListingFragment["insertionId"];
  trackingProperties?: TrackingProperties;
  disabled: boolean;
  openInNewTab: boolean;
  listingIndex: number;
  leader: ActivityListing_ListingFragment["leader"];
}

/**
 * Title for the search results gallery experiment listing card.
 *
 * Major changes from the original Title component from ActivityListing:
 * - Now rendered as a Link component (instead of a Typography component).
 * - Handles tracking of product clicks and impressions.
 *
 * These changes are to suppor the new delegation-based approach to clicks on the listing card (see SearchResultsGalleryExperimentListing.tsx).
 */
const Title = React.forwardRef<HTMLAnchorElement, TitleProps>(
  (
    {
      activity,
      insertionId,
      trackingProperties,
      disabled,
      openInNewTab,
      listingIndex,
      leader,
    },
    ref,
  ) => {
    const trackProductClicked = useProductClicked();
    const Link = useLinkComponent();
    const linkPath = useLinkPath({ activity });

    const LinkComponent = React.forwardRef<
      HTMLAnchorElement,
      { children: React.ReactNode; sx?: SystemStyleObject }
    >(({ children, sx = {} }, forwardedRef) => {
      const sharedProperties: Record<string, string> = {
        ...trackingProperties,
        activity_uid: activity?.uid,
        // Only include insertion_id when non-nullish
        ...(insertionId ? { insertion_id: insertionId } : {}),
      };

      return (
        <Link
          ref={forwardedRef}
          to={linkPath}
          trackingName={"Activity Impression"}
          sharedProperties={sharedProperties}
          trackingTouchEventName="Activity Impression Click"
          trackingUniqueId={activity?.uid}
          disabled={disabled}
          tabIndex={disabled ? -1 : undefined}
          useLinkTracking
          onClick={(e: React.MouseEvent) => {
            if (disabled) {
              e.preventDefault();
              return;
            }
            trackProductClicked({ activity, leader }, listingIndex);
          }}
          style={sx ?? {}}
          target={openInNewTab ? "_blank" : undefined}
          rel={openInNewTab ? "noopener noreferrer" : undefined}
        >
          {children}
        </Link>
      );
    });

    LinkComponent.displayName = "LinkComponent";

    return (
      <LinkComponent
        ref={ref}
        sx={{
          textDecoration: "none",
          width: "100%",
          height: "45px",
          display: "inline-block",
        }}
      >
        <Typography
          data-test-id="activity-title"
          variant="h6"
          emphasized
          sx={{
            overflow: "hidden",
            wordBreak: "break-word",
            alignSelf: "top",
            color: "text.primary",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            textOverflow: "ellipsis",
          }}
        >
          {activity.titleTranslated}
        </Typography>
      </LinkComponent>
    );
  },
);

Title.displayName = "Title";

export default Title;
